import {type ComponentProps, ReactNode, useCallback} from 'react';
import {view} from '@sail/ui';
import {
  Conversation,
  InlineDataResult as ConversationsClientInlineDataResult,
  InlineDataResultAction,
  InlineDataConversationMessage,
} from '@stripe-internal/conversations-client';
import {InlineDataProps, InlineDataResult} from '@stripe-internal/inline-data';
import {
  InlineDataRow,
  type Props as InlineDataRowComponentProps,
} from '../../../Chat/Row/InlineDataRow';
import {InlineDataStripeAssistantMessageRow} from './InlineDataStripeAssistantMessageRow';

type Props = {
  conversation: Conversation;
  message: InlineDataConversationMessage;
  children?: ReactNode;
  avatar?: ReactNode | null;

  // Optional renderer overrides
  ChatRowComponent?: InlineDataRowComponentProps['ChatRowComponent'];
  ChatBubbleComponent?: InlineDataRowComponentProps['ChatBubbleComponent'];
  LiveAnswersRowComponent?: ComponentProps<
    typeof InlineDataStripeAssistantMessageRow
  >['LiveAnswersRowComponent'];
};

export const InlineDataMessageRow = ({
  conversation,
  message,
  avatar,
  children,
  ChatRowComponent,
  ChatBubbleComponent,
  LiveAnswersRowComponent,
}: Props) => {
  const inlineData = message.inlineData as InlineDataProps;

  const onDone = useCallback(
    (result: InlineDataResult) => {
      const action: InlineDataResultAction = {
        name: 'inline-data-result',
        message,
        result: result as ConversationsClientInlineDataResult,
      };
      return conversation.action(action);
    },
    [conversation, message],
  );

  // until we have a sanctioned path for stripe assistant messages,
  // we're overloading inline data messages. we shortcut here and
  // return an appropriate stripe assistant row
  if (inlineData.name === 'stripe_assistant') {
    return (
      <InlineDataStripeAssistantMessageRow
        conversation={conversation}
        message={message}
        avatar={avatar}
        LiveAnswersRowComponent={LiveAnswersRowComponent}
      >
        {children}
      </InlineDataStripeAssistantMessageRow>
    );
  }

  const background =
    message.participant.id === conversation.user.id
      ? 'neutral600'
      : 'neutral100';

  const position =
    message.participant.id === conversation.user.id ? 'end' : 'start';

  const style =
    inlineData.name === 'merchant_verification_status'
      ? 'outer_bubble'
      : message.inlineData.style;

  return (
    <view.div
      data-testid="inline-data-message-row"
      className="uh-conversations-inline-data-message-row"
      data-message-type={message.participant.role}
    >
      <InlineDataRow
        inlineData={{
          ...inlineData,
          context: inlineData.context ?? {
            conversation_id: conversation.id,
            effect_id: message.effectId,
          },
          onDone,
        }}
        background={background}
        position={position}
        avatar={avatar}
        style={style}
        ChatRowComponent={ChatRowComponent}
        ChatBubbleComponent={ChatBubbleComponent}
      >
        {children}
      </InlineDataRow>
    </view.div>
  );
};
